import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Autocomplete, useApiMutation } from '@/modules/common';
import {
  EventParticipantType,
  EventProductResponseItem,
} from '@/modules/events/types';
import { EventParticipant, EventProduct } from '@/modules/eventsV2/types';

import { editParticipantEventSchema } from '../schema';

interface ParticipantDetailsProps {
  isOpen: boolean;
  eventId: string;
  selectedParticipant: EventParticipant;
  participantTypes: EventParticipantType[];
  onOpenChange: (open: boolean) => void;
}

export type FormValues = z.infer<typeof editParticipantEventSchema>;

export function ParticipantDetailsDialog({
  isOpen,
  eventId,
  participantTypes,
  onOpenChange,
  selectedParticipant,
}: ParticipantDetailsProps) {
  const queryClient = useQueryClient();

  const {
    id: participantId,
    firstName,
    lastName,
    email,
    phoneNumber,
    type,
    status,
    usagesLeft,
    company,
    jobTitle,
    notificationNumbers,
  } = selectedParticipant;

  const form = useForm<FormValues>({
    resolver: zodResolver(editParticipantEventSchema),
    defaultValues: {
      //POTENTIAL BUG: type should be the id not a label
      type: participantTypes.find((t) => t.value === type)?.id || '',
      usagesLeft,
    },
  });

  const updateParticipantMutation = useApiMutation<{
    typeId: string;
    usagesLeft: number;
  }>(
    `/events/${eventId}/participants/${participantId}`,
    {
      method: 'PATCH',
    },
    {
      onError: (error) => {
        toast.error(error?.message);
      },
    }
  );

  const handleUpdateParticipant = async ({ type, usagesLeft }: FormValues) => {
    try {
      await updateParticipantMutation.mutateAsync({
        typeId: type,
        usagesLeft: +usagesLeft,
      });
      queryClient.invalidateQueries({ queryKey: ['eventParticipants'] });
      onOpenChange(false);
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  const disabledFields = [
    {
      label: 'Full Name',
      value: `${firstName} ${lastName}`,
    },
    {
      label: 'Email Address',
      value: email,
    },
    {
      label: 'Company',
      value: company,
    },
    {
      label: 'Job Title',
      value: jobTitle,
    },
    {
      label: 'Status',
      value: status,
    },
    {
      label: 'Phone Number',
      value: phoneNumber,
    },
    {
      label: 'Other',
      value: '',
    },
    {
      label: 'Alert Recipients',
      value: notificationNumbers.length,
    },
  ];

  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      <DialogContent>
        <Form {...form}>
          <DialogHeader>
            <DialogTitle>Participant Details</DialogTitle>
            <DialogDescription>Description</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <Autocomplete
              label="Type"
              control={form.control}
              name={'type'}
              placeholder="Select type"
              options={participantTypes}
            />
            <FormField
              control={form.control}
              name="usagesLeft"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Attempts Left</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter attempts"
                      {...field}
                      min={0}
                      max={1000}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {disabledFields.map(({ label, value }) => (
              <FormItem key={label}>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input value={value || '-'} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            ))}
          </div>

          <DialogFooter className="mt-6 gap-4">
            <DialogClose asChild>
              <Button variant="outline" data-testid="cancel-button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              data-testid="save-button"
              onClick={form.handleSubmit(handleUpdateParticipant)}
              isLoading={updateParticipantMutation.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
